// © 2015-2021 WDNA - All Rights Reserved

import _ from 'lodash';
import * as React from 'react';

import { RowDataUpdatedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { removeTab } from 'wdna-components/lib/tabs';
import { messageError, messageInfo, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { lov, LovDO, typeSensor, TypeSensorDO } from '../../../configuration/requests';
import { waste, sensor, SensorDO, WasteInfoDO, WasteContainerDO } from '../../requests';

import ButtonsForm from '../../../../components/ButtonsForm';
import RecalibrateModal from './RecalibrateModal';
import Form from './Form';

const MIN_UNLOADS: Map<string, number> = new Map<string, number>([
  ['FOR', 10],
  ['GLA', 10],
  ['PAP', 7],
  ['PET', 7],
  ['PNG', 10],
  ['CRK', 10],
  ['SUW', 10],
]);

const MAX_WEIGHT: Map<string, number> = new Map<string, number>([
  ['120', 60],
  ['240', 100],
  ['800', 320],
  ['1000', 400],
]);

interface Props {
  rowId?: number;
  notify: () => void;
  wasteContainers?: WasteContainerDO[];
}

interface State {
  loading: boolean;
  showGrid?: boolean;

  sensorArray?: SensorDO[];
  modelWasteArray?: LovDO[];
  typeWasteArray?: LovDO[];
  wasteInfoTable?: WasteInfoDO[];
  isNew: boolean;
  newRecal?: boolean;
  showModal?: boolean;

  data: Partial<WasteContainerDO>;
  info: Partial<WasteInfoDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private buttons: TableButtonsConfig;
  private defaultValues: any;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {};
    this.buttons = {
      add: true,
      edit: true,
      del: false,
    };

    this.state = {
      loading: false,
      showGrid: false,
      showModal: false,

      isNew: true,
      newRecal: false,
      sensorArray: [],
      modelWasteArray: [],
      typeWasteArray: [],

      data: {
        date: new Date(),
        minUnload: 0,
        maxWeight: 0,
        minTemp: 1,
        maxTemp: 50,
        active: false
      },

      info: {
        date: new Date(),
        ratio1: 0,
        ratio2: 0,
        ratio3: 0,
        ratio4: 0,
      },
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    const { rowId, wasteContainers } = this.props || '';
    const isNew: boolean = (rowId === undefined || !!isNaN(rowId));

    this.loadingStart();
    try {
      let wasteInfoTable: WasteInfoDO[] = [];
      const sensorArray: SensorDO[] = [];
      let wasteRow: Partial<WasteContainerDO> | undefined = this.state.data;
      if (isNew) {
        // Promises to get sensors of type waste
        const typeSensorList: TypeSensorDO[] = await typeSensor<TypeSensorDO[]>('list', undefined, { type: 'WST' }) || [];
        const promises: any[] = [];
        typeSensorList.forEach((item: TypeSensorDO) => {
          promises.push(sensor<SensorDO[]>('list', undefined, { typeSensorId: item.id }));
        });
        const results: SensorDO[][] = await Promise.all(promises);
        results.forEach((r: SensorDO[]) => { sensorArray.push(...r); });

        // Get IDs in sensors and not in wasteContainers, and filter the sensors with those IDs
        // That way, the selector will only have type WST sensors which have not been already created
        const availableIDs: Set<number> = this.getAvailableIDs(sensorArray, wasteContainers || []);
        _.remove(sensorArray, (sens: SensorDO) => !availableIDs.has(sens.id));
        sensorArray.sort((a: SensorDO, b: SensorDO) => (_.toNumber(a.code) - _.toNumber(b.code)));
      } else {
        let sensorChosen: SensorDO | undefined;
        [wasteInfoTable, wasteRow, sensorChosen] = await Promise.all([
          waste<WasteInfoDO[]>('info_list', String(rowId)) || [],
          waste<WasteContainerDO>('get', String(rowId)),
          sensor<SensorDO>('get', String(rowId)),
        ]);

        if (sensorChosen) sensorArray.push(sensorChosen);
        this.buttons.del = wasteInfoTable && wasteInfoTable.length > 1;
      }

      const modelWasteArray: LovDO[] = await lov<LovDO[]>('statics', 'model_container') || [];
      const typeWasteArray: LovDO[] = await lov<LovDO[]>('statics', 'type_waste') || [];

      this.setLocalState(
        {
          data: {
            ...wasteRow,
            date: new Date(wasteRow?.date || ''),
          },
          wasteInfoTable,
          sensorArray,
          modelWasteArray,
          typeWasteArray,
          isNew,
        },
        () => {
          this.defaultValues = _.cloneDeep(this.state.data);
        }
      );
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadTable = async (): Promise<void> => {
    const { rowId } = this.props || '';
    this.loadingStart();
    try {
      const wasteInfoTable: WasteInfoDO[] = await waste<WasteInfoDO[]>('info_list', String(rowId)) || [];
      this.buttons.del = wasteInfoTable.length > 1;
      this.setLocalState({ wasteInfoTable });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public getAvailableIDs = (sensorList: SensorDO[], wasteContainers: WasteContainerDO[]): Set<number> => {
    const sensorIDs: Set<number> = sensorList.reduce((result: Set<number>, item: SensorDO) => {
      result.add(item.id);
      return result;
    }, new Set<number>([]));

    const wasteContainersIDs: Set<number> = wasteContainers.reduce((result: Set<number>, item: WasteContainerDO) => {
      result.add(item.id);
      return result;
    }, new Set<number>([]));

    const availableIDs: Set<number> = new Set([...sensorIDs].filter((x: number) => !wasteContainersIDs.has(x)));
    return availableIDs;
  };

  public loadingStart = (): void => {
    this.setLocalState({ loading: true });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setLocalState({ loading: false });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setLocalState({ data: { ...this.defaultValues } });
  };

  public submit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const { isNew, data, info } = this.state;

    if (!this.isValidForm(data)) return;
    if (isNew && !this.isValidInfo(info as WasteInfoDO)) return;

    this.loadingStart();
    try {
      const saved: WasteContainerDO | undefined = await waste<WasteContainerDO>('set', undefined, data);
      if (isNew && saved) {
        info.containerId = saved.id;
        info.date = saved.date;
        await waste('info_set', undefined, info);
      }
      messageInfo('Success', `Sensor successfully ${data.id ? 'edited' : 'created'} ${data.id}`, 5000);
      const { notify } = this.props;
      if (typeof notify === 'function') {
        notify();
      }
      window.store.dispatch(removeTab('WE'));
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public isValidForm = (data: Partial<WasteContainerDO>): boolean => {
    const { id, type, model } = data;
    if (!id) {
      messageError('Error', 'Please select a sensor');
      return false;
    }
    if (!type) {
      messageError('Error', 'Please select the type of the container');
      return false;
    }
    if (!model) {
      messageError('Error', 'Please select the model of the container');
      return false;
    }
    return true;
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | Date | null): void => {
    if (e instanceof Date) {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          date: e,
        },
      }));
    } else {
      const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
      const { ctype } = dataset || {};
      // Process new value from ctype
      let newValue: any = (type === 'checkbox') ? !!checked : value;
      if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
      // Create new data state structure
      const levels: string[] = name.split('.');
      const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
      _.set(nextState, levels, newValue);

      if (name === 'data.type') nextState.minUnload = MIN_UNLOADS.get(String(value)) || 0;
      if (name === 'data.size') nextState.maxWeight = MAX_WEIGHT.get(String(value)) || 0;

      this.setLocalState(nextState);
    }
  };

  public handleInfoChange = (e: React.ChangeEvent<HTMLInputElement> | Date | null): void => {
    if (e instanceof Date) {
      this.setState((prevState: State) => ({
        info: {
          ...prevState.info,
          date: e,
        },
      }));
    } else {
      this.handleChange(e);
    }
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({ showGrid: true });
  };

  public handleInfo = (value: WasteInfoDO): void => {
    const rowDate: Date | undefined = value.date;
    this.setState((prevState: State) => ({
      showModal: true,
      newRecal: (rowDate === undefined),
      info: {
        containerId: prevState.data.id,
        date: rowDate || new Date(),
        ratio1: value.ratio1 || 0,
        ratio2: value.ratio2 || 0,
        ratio3: value.ratio3 || 0,
        ratio4: value.ratio4 || 0,
      }
    }));
  };

  public handleInfoCancel = (_e?: React.MouseEvent<HTMLButtonElement>): void => {
    this.setLocalState({ showModal: false });
  };

  public handleInfoDelete = (value: WasteInfoDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', (): void => {
      this.loadingStart();
      waste('info_del', String(value.containerId), { date: new Date(value.date) })
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.loadTable().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public handleInfoSave = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    const { info } = this.state;

    if (!this.isValidInfo(info as WasteInfoDO)) return;

    this.loadingStart();
    waste('info_set', undefined, info)
      ?.then(() => {
        messageInfo('Success', `Recalibration successful for container ${info.containerId}`, 5000);
        this.loadTable().catch(() => { /* pass */ });
        this.handleInfoCancel();
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public isValidInfo = (data: WasteInfoDO): boolean => {
    const { ratio1, ratio2, ratio3, ratio4 } = data;
    if (isNaN(ratio1) || isNaN(ratio2) || isNaN(ratio3) || isNaN(ratio4)) {
      messageError('Error', 'Please enter valid parameters');
      return false;
    }
    /*
    if (ratio1 >= 100 || ratio2 >= 100 || ratio3 >= 100 || ratio4 >= 100) {
      messageError('Error', 'The ratios of the wheels must be below 100%');
      return false;
    }
    */
    return true;
  };

  public autoFormMin = (data: WasteContainerDO): number => {
    const { type } = data;
    return MIN_UNLOADS.get(type) || 0;
  };

  public autoFormMax = (data: WasteContainerDO): number => {
    const { size } = data;
    return MAX_WEIGHT.get(String(size)) || 0;
  };

  public render(): JSX.Element {
    const { loading, data, info, newRecal, isNew, showGrid, wasteInfoTable, showModal } = this.state;

    const columnsToHide: string[] = ['containerId'];
    const columnAliases: Record<string, string> = {
      date: 'Date',
      ratio1: 'Ratio Wheel-1',
      ratio2: 'Ratio Wheel-2',
      ratio3: 'Ratio Wheel-3',
      ratio4: 'Ratio Wheel-4',
    };
    const columnStyles: Record<string, any> = {
      date: { textAlign: 'center' },
      ratio1: { textAlign: 'right' },
      ratio2: { textAlign: 'right' },
      ratio3: { textAlign: 'right' },
      ratio4: { textAlign: 'right' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form
                      className="smart-form"
                      onSubmit={(e: React.FormEvent<HTMLFormElement>): void => { this.submit(e).catch(() => { /* pass */ }); }}
                      method="post"
                      encType="multipart/form-data"
                    >
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Waste-Container {isNew ? 'New' : `Edit: ${data.id}`}
                        </h2>
                      </header>
                      <Form
                        duplicate={false}
                        formValues={this.state}
                        onChange={this.handleChange}
                        onChangeInfo={this.handleInfoChange}
                        onAutoFormMin={this.autoFormMin}
                        onAutoFormMax={this.autoFormMax}
                      />
                      <ButtonsForm loading={loading} disabled={!hasLevel('role', ['ADM', 'URW'])} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
              {!isNew && (
                <div className="infoWaste">
                  <RecalibrateModal
                    info={info}
                    show={showModal}
                    newRecal={newRecal}
                    title={`Recalibrate container ${data.id || ''}`}
                    onSave={this.handleInfoSave}
                    onChange={this.handleInfoChange}
                    onCancel={this.handleInfoCancel}
                  />
                  <div id="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
                    <Table
                      data={wasteInfoTable || []}
                      hiddenCols={columnsToHide}
                      alias={columnAliases}
                      customCellStyles={columnStyles}
                      buttons={this.buttons}
                      loading={loading}
                      rowDataUpdated={this.rowDataUpdated}
                      onAdd={this.handleInfo}
                      onEdit={this.handleInfo}
                      onDelete={this.handleInfoDelete}
                      height="calc(40vh)"
                    />
                  </div>
                </div>
              )}
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
